import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiAlertCircle, FiCheck, FiChevronDown, FiChevronRight, FiClock } from 'react-icons/fi';
import {
  clientPortalService,
  type ClientPortalCheckpointHit,
  type ClientPortalContext,
  type ClientPortalPatrolSweep,
  type ClientPortalScope,
} from '../../services/clientPortalService';
import { useI18n } from '../../i18n';
import { formatDateTime, formatDuration, formatLateness, formatTime } from './clientPortalFormat';
import './ClientPortal.css';

interface Props {
  context: ClientPortalContext;
  siteId: string | null;
  can: (scope: ClientPortalScope) => boolean;
}

type RangeKey = '24h' | '7d' | '30d';

const RANGES: Array<{ key: RangeKey; label: string; days: number }> = [
  { key: '24h', label: 'Last 24 hours', days: 1 },
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
];

export default function ClientToursPage({ context, siteId, can }: Props) {
  const { t } = useI18n();
  const [range, setRange] = useState<RangeKey>('7d');
  const [sweeps, setSweeps] = useState<ClientPortalPatrolSweep[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [checkpoints, setCheckpoints] = useState<Record<string, ClientPortalCheckpointHit[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const days = RANGES.find((item) => item.key === range)?.days ?? 7;
      const rows = await clientPortalService.fetchPatrolProgress(
        new Date(Date.now() - days * 24 * 60 * 60 * 1000),
        new Date(),
        siteId,
      );
      setSweeps(rows);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [range, siteId]);

  useEffect(() => {
    void load();
  }, [load]);

  const summary = useMemo(() => ({
    total: sweeps.length,
    completed: sweeps.filter((sweep) => sweep.status === 'completed').length,
    missed: sweeps.filter((sweep) => sweep.status === 'missed').length,
    late: sweeps.filter((sweep) => sweep.startedLateBySeconds >= 300).length,
  }), [sweeps]);

  const toggle = async (sweep: ClientPortalPatrolSweep) => {
    if (expandedId === sweep.segmentId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(sweep.segmentId);
    if (checkpoints[sweep.segmentId]) return;
    try {
      const hits = await clientPortalService.fetchPatrolCheckpoints(sweep.segmentId);
      setCheckpoints((prev) => ({ ...prev, [sweep.segmentId]: hits }));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  if (!can('patrol_records') && !can('patrol_progress')) {
    return (
      <div className="cp-page">
        <div className="cp-notice">
          <FiAlertCircle aria-hidden="true" />
          <div>
            <strong>{t('Patrol records are not enabled')}</strong>
            <p>{t('Your security provider controls which reports reach this portal.')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-page">
      {error ? <div className="cp-banner cp-banner--error">{error}</div> : null}

      <div className="cp-tabs" role="tablist">
        {RANGES.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={item.key === range}
            className={`cp-tab${item.key === range ? ' cp-tab--active' : ''}`}
            onClick={() => setRange(item.key)}
          >
            {t(item.label)}
          </button>
        ))}
      </div>

      <section className="cp-stat-row">
        <article className="cp-stat">
          <span>{t('Sweeps scheduled')}</span>
          <strong>{summary.total}</strong>
        </article>
        <article className="cp-stat">
          <span>{t('Completed')}</span>
          <strong>{summary.completed}</strong>
        </article>
        <article className={`cp-stat${summary.late > 0 ? ' cp-stat--warn' : ''}`}>
          <span>{t('Started late')}</span>
          <strong>{summary.late}</strong>
        </article>
        <article className={`cp-stat${summary.missed > 0 ? ' cp-stat--alert' : ''}`}>
          <span>{t('Missed')}</span>
          <strong>{summary.missed}</strong>
        </article>
      </section>

      <article className="cp-panel cp-panel--flush">
        {isLoading ? (
          <div className="cp-loading"><div className="spinner" /></div>
        ) : sweeps.length === 0 ? (
          <div className="cp-empty">{t('No patrol sweeps were recorded in this period.')}</div>
        ) : (
          <ul className="cp-tour-list">
            {sweeps.map((sweep) => {
              const isOpen = expandedId === sweep.segmentId;
              const lateness = formatLateness(sweep.startedLateBySeconds);
              const hits = checkpoints[sweep.segmentId] ?? [];
              return (
                <li key={sweep.segmentId} className="cp-tour">
                  <button
                    type="button"
                    className="cp-tour__head"
                    aria-expanded={isOpen}
                    onClick={() => void toggle(sweep)}
                  >
                    {isOpen ? <FiChevronDown aria-hidden="true" /> : <FiChevronRight aria-hidden="true" />}
                    <div className="cp-tour__title">
                      <strong>{sweep.patrolName ?? t('Patrol')}</strong>
                      <span>{sweep.siteName} · {sweep.guardName ?? t('Officer on duty')}</span>
                    </div>
                    <div className="cp-tour__meta">
                      <span className={`cp-chip cp-chip--${sweep.status}`}>
                        {sweep.status === 'completed'
                          ? t('Completed')
                          : sweep.status === 'missed'
                            ? t('Missed')
                            : sweep.status === 'in_progress'
                              ? t('In progress')
                              : sweep.status === 'skipped'
                                ? t('Skipped')
                                : t('Scheduled')}
                      </span>
                      {lateness ? <span className="cp-chip cp-chip--warn">{lateness}</span> : null}
                      <span>
                        {t('{done}/{total} checkpoints', {
                          done: sweep.checkpointsCompleted,
                          total: sweep.checkpointsTotal,
                        })}
                      </span>
                      <span>{formatDateTime(sweep.plannedStartAt)}</span>
                    </div>
                  </button>

                  {isOpen ? (
                    <div className="cp-tour__body">
                      <dl className="cp-facts cp-facts--inline">
                        <div>
                          <dt>{t('Scheduled')}</dt>
                          <dd>{formatTime(sweep.plannedStartAt)} – {formatTime(sweep.plannedEndAt)}</dd>
                        </div>
                        <div>
                          <dt>{t('Actual')}</dt>
                          <dd>
                            {sweep.actualStartedAt
                              ? `${formatTime(sweep.actualStartedAt)} – ${sweep.actualEndedAt ? formatTime(sweep.actualEndedAt) : t('ongoing')}`
                              : t('Not started')}
                          </dd>
                        </div>
                        <div>
                          <dt>{t('Duration')}</dt>
                          <dd>{formatDuration(sweep.actualStartedAt, sweep.actualEndedAt)}</dd>
                        </div>
                      </dl>

                      {hits.length === 0 ? (
                        <div className="cp-empty cp-empty--sm">{t('No checkpoint scans were recorded for this sweep.')}</div>
                      ) : (
                        <ol className="cp-checkpoints">
                          {hits.map((hit, index) => (
                            <li key={`${hit.checkpointId ?? index}-${hit.recordedAt}`}>
                              <span className={`cp-checkpoint__dot cp-checkpoint__dot--${hit.status}`} aria-hidden="true">
                                {hit.status === 'completed' ? <FiCheck /> : <FiClock />}
                              </span>
                              <div>
                                <strong>{hit.checkpointName ?? t('Checkpoint')}</strong>
                                {hit.checkpointCode ? <span className="cp-code">{hit.checkpointCode}</span> : null}
                                {hit.notes ? <p>{hit.notes}</p> : null}
                              </div>
                              <div className="cp-checkpoint__meta">
                                <span>{formatTime(hit.recordedAt)}</span>
                                {hit.withinGeofence === false ? (
                                  <span className="cp-chip cp-chip--warn">{t('Outside geofence')}</span>
                                ) : null}
                                {formatLateness(hit.completedLateBySeconds) ? (
                                  <span className="cp-chip cp-chip--warn">
                                    {formatLateness(hit.completedLateBySeconds)}
                                  </span>
                                ) : null}
                              </div>
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </article>

      {context.sites.length > 1 && !siteId ? (
        <p className="cp-footnote">
          {t('Showing every site on your contract. Use the site selector to narrow this view.')}
        </p>
      ) : null}
    </div>
  );
}
